import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Check, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

export type ScanStage = 'probing' | 'uploading' | 'reading' | 'planning' | 'error';

interface ReferenceScanProps {
    kind: 'video' | 'image';
    url: string;
    frameUrls: string[];
    aspect?: number;
    stage: ScanStage;
    error?: string | null;
    onRemove?: () => void;
}

const SCAN_CSS = `
@keyframes studio-scan { 0% { transform: translateY(-110%); } 100% { transform: translateY(330%); } }
.studio-scan { animation: studio-scan 3.4s cubic-bezier(.45,.05,.55,.95) infinite; }
@media (prefers-reduced-motion: reduce) { .studio-scan { animation: none; opacity: 0; } }
`;

const ORDER: ScanStage[] = ['probing', 'uploading', 'reading', 'planning'];

/**
 * The reference being studied, in the middle of the page: the clip plays in a
 * soft viewfinder with a slow scan line, its cuts appear under it one by one,
 * and beside it a short list says what is happening now — finding the cuts,
 * saving it, watching every shot, planning yours.
 *
 * Replaces a banner that only showed up once the upload was done, which left
 * the first seconds after "go" looking like nothing was happening.
 */
export const ReferenceScan: React.FC<ReferenceScanProps> = ({ kind, url, frameUrls, aspect, stage, error, onRemove }) => {
    const { t } = useTranslation();
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const id = window.setInterval(() => setTick((n) => n + 1), 2400);
        return () => window.clearInterval(id);
    }, []);

    const watching = [
        t('STUDIO.SCAN.W_CAMERA', 'Measuring the camera height and angle…'),
        t('STUDIO.SCAN.W_LIGHT', 'Reading the light and the colours…'),
        t('STUDIO.SCAN.W_TEXT', 'Spotting the words on screen…'),
        t('STUDIO.SCAN.W_MOVES', 'Following how the camera moves…'),
        t('STUDIO.SCAN.W_TIMING', 'Timing every cut…'),
        t('STUDIO.SCAN.W_SETS', 'Noticing which shots share a set…'),
    ];
    const planning = [
        t('STUDIO.SCAN.P_SHOTS', 'Matching each shot to your products…'),
        t('STUDIO.SCAN.P_SCENES', 'Writing the scenes…'),
        t('STUDIO.SCAN.P_FRAMING', 'Choosing the framing…'),
    ];
    const steps: Array<{ id: ScanStage; label: string; done?: string; live?: string }> = [
        { id: 'probing', label: t('STUDIO.SCAN.CUTS', 'Finding the cuts'), done: kind === 'video' && frameUrls.length ? (frameUrls.length === 1 ? t('STUDIO.SCAN.CUTS_ONE', 'One continuous shot') : t('STUDIO.SCAN.CUTS_DONE', '{{n}} shots found', { n: frameUrls.length })) : undefined },
        { id: 'uploading', label: t('STUDIO.SCAN.SAVE', 'Adding it to your production') },
        { id: 'reading', label: t('STUDIO.SCAN.WATCH', 'Watching every shot'), live: watching[tick % watching.length] },
        { id: 'planning', label: t('STUDIO.SCAN.PLAN', 'Planning your shots'), live: planning[tick % planning.length] },
    ];
    const at = stage === 'error' ? -1 : ORDER.indexOf(stage);
    const ratio = aspect || (kind === 'video' ? 9 / 16 : 4 / 5);
    const reading = stage === 'reading';

    return (
        <div className="mx-auto grid w-full max-w-4xl items-center gap-8 px-2 py-6 animate-in fade-in duration-500 md:grid-cols-[minmax(0,340px)_1fr] md:gap-12 md:py-10">
            <style>{SCAN_CSS}</style>
            <div className="flex flex-col items-center gap-3">
                <div
                    className="relative w-full max-w-[340px] overflow-hidden rounded-2xl border bg-neutral-900 shadow-[0_20px_50px_-24px_rgba(28,25,23,0.45)]"
                    style={{ aspectRatio: String(ratio), maxHeight: '58vh' }}
                >
                    {kind === 'video'
                        ? <video src={url} autoPlay muted loop playsInline className="absolute inset-0 size-full object-cover" />
                        : <img src={url} alt="" className="absolute inset-0 size-full object-cover" />}
                    {/* A faint viewfinder grid, the scan line and its glow. */}
                    <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.07)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.07)_1px,transparent_1px)] bg-[size:33.33%_33.33%]" />
                    {stage !== 'error' && (
                        <div className="studio-scan pointer-events-none absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-transparent via-white/[0.10] to-white/[0.22] after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-white/80 after:shadow-[0_0_12px_2px_rgba(255,255,255,0.55)]" />
                    )}
                    {(['start-3 top-3 border-s-2 border-t-2 rounded-ss-md', 'end-3 top-3 border-e-2 border-t-2 rounded-se-md', 'start-3 bottom-3 border-s-2 border-b-2 rounded-es-md', 'end-3 bottom-3 border-e-2 border-b-2 rounded-ee-md']).map((c) => (
                        <span key={c} className={cn('pointer-events-none absolute size-5 border-white/80', c)} />
                    ))}
                </div>
                {frameUrls.length > 1 && (
                    <div className="flex max-w-[340px] flex-wrap justify-center gap-1.5">
                        {frameUrls.map((f, i) => (
                            <span key={f} className="animate-in fade-in zoom-in-90 fill-mode-both duration-500" style={{ animationDelay: `${i * 90}ms` }}>
                                <img src={f} alt="" className={cn(
                                    'h-14 rounded-md border object-cover transition-[opacity,box-shadow] duration-500',
                                    reading && tick % frameUrls.length === i ? 'opacity-100 ring-2 ring-foreground/20' : reading ? 'opacity-55' : 'opacity-100',
                                )} style={{ aspectRatio: String(ratio) }} />
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <div className="min-w-0">
                <div className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">{t('STUDIO.SCAN.EYEBROW', 'Your reference')}</div>
                <h2 className="mt-1.5 text-balance text-xl font-medium tracking-tight text-foreground">
                    {stage === 'error' ? t('STUDIO.SCAN.FAILED', 'This one could not be read') : stage === 'planning' ? t('STUDIO.SCAN.TITLE_PLAN', 'Turning it into your shots') : t('STUDIO.SCAN.TITLE', 'Reading your reference')}
                </h2>
                <p className="mt-1.5 max-w-md text-sm text-muted-foreground">
                    {stage === 'error' ? (error || '') : t('STUDIO.SCAN.SUB', 'Ardi studies it shot by shot — the framing, the light, the moves and the words — so yours can follow the same rhythm.')}
                </p>

                {stage === 'error' ? (
                    <div className="mt-5 flex items-center gap-2">
                        <AlertCircle className="size-4 text-destructive" />
                        {onRemove && <Button variant="outline" size="sm" className="gap-1.5" onClick={onRemove}><X className="size-3.5" />{t('STUDIO.SCAN.REMOVE', 'Remove the reference')}</Button>}
                    </div>
                ) : (
                    <ol className="mt-6 space-y-1">
                        {steps.map((s, i) => {
                            const done = i < at;
                            const live = i === at;
                            return (
                                <li key={s.id} className={cn('flex gap-3 rounded-xl px-3 py-2.5 transition-colors duration-500', live && 'bg-accent/60')}>
                                    <span className={cn(
                                        'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors duration-500',
                                        done ? 'border-foreground/15 bg-foreground/[0.06] text-foreground' : live ? 'border-transparent' : 'border-input',
                                    )}>
                                        {done ? <Check className="size-3" /> : live ? <Spinner className="size-4" /> : null}
                                    </span>
                                    <div className="min-w-0">
                                        <div className={cn('text-sm', done || live ? 'text-foreground' : 'text-muted-foreground')}>{s.label}</div>
                                        {done && s.done && <div className="text-xs text-muted-foreground">{s.done}</div>}
                                        {live && s.live && (
                                            <div key={tick} className="text-xs text-muted-foreground animate-in fade-in slide-in-from-bottom-1 duration-500">{s.live}</div>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                )}
            </div>
        </div>
    );
};
